import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import type { ReviewPhotoToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { serverMessage } from '~/lib/api/server-error';
import { useDeleteReviewPhoto } from '~/hooks/use-reviews';

/** Grid cell: the first photos of a customer comment as small thumbnails, "+N" for the rest. */
export function ReviewPhotoStrip({ photos, max = 3 }: { photos: ReviewPhotoToken[] | null | undefined; max?: number }) {
	const { t } = useTranslation();
	if (!photos?.length) return null;
	const shown = photos.slice(0, max);
	return (
		<div className="mt-1 flex items-center gap-1" aria-label={t('catalog.reviews.photos.count', { count: photos.length })}>
			{shown.map(p => (
				<img key={p.id} src={p.url ?? ''} alt="" loading="lazy" className="size-8 rounded border border-border object-cover" />
			))}
			{photos.length > max && <span className="text-[11px] text-foreground-muted">+{photos.length - max}</span>}
		</div>
	);
}

interface GalleryProps {
	photos: ReviewPhotoToken[] | null | undefined;
}

/**
 * The photos of a customer comment, larger: click opens a lightbox (previous / next), and each can be removed
 * (DeleteReviewPhoto) after a confirm. A removed photo disappears here at once; the grid refreshes on its own.
 */
export function ReviewPhotoGallery({ photos }: GalleryProps) {
	const { t } = useTranslation();
	const remove = useDeleteReviewPhoto();
	const [removed, setRemoved] = useState<Set<number>>(new Set());
	const [viewIndex, setViewIndex] = useState<number | null>(null);
	const [confirmId, setConfirmId] = useState<number | null>(null);

	const visible = (photos ?? []).filter(p => !removed.has(p.id ?? 0));
	if (visible.length === 0) return null;
	const viewing = viewIndex != null ? visible[viewIndex] : undefined;

	const confirmDelete = () => {
		if (confirmId == null) return;
		const id = confirmId;
		remove.mutate(id, {
			onSuccess: () => {
				setRemoved(prev => new Set(prev).add(id));
				setConfirmId(null);
				setViewIndex(null);
				toast.success(t('catalog.reviews.photos.deleted'));
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<div className="flex flex-wrap gap-2">
				{visible.map((p, index) => (
					<div key={p.id} className="group relative">
						<button
							type="button"
							onClick={() => setViewIndex(index)}
							className="block overflow-hidden rounded-md border border-border"
							aria-label={t('catalog.reviews.photos.open')}>
							<img src={p.url ?? ''} alt="" loading="lazy" className="size-20 object-cover" />
						</button>
						<Button
							type="button"
							variant="destructive"
							size="icon"
							className="absolute top-1 right-1 size-6 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
							aria-label={t('catalog.reviews.photos.delete')}
							title={t('catalog.reviews.photos.delete')}
							onClick={() => setConfirmId(p.id ?? null)}>
							<Trash2 size={12} />
						</Button>
					</div>
				))}
			</div>

			<Dialog open={viewing != null} onOpenChange={open => !open && setViewIndex(null)}>
				<DialogContent className="sm:max-w-3xl">
					<DialogHeader>
						<DialogTitle>{t('catalog.reviews.photos.title')}</DialogTitle>
						<DialogDescription>{viewIndex != null ? `${viewIndex + 1} / ${visible.length}` : ''}</DialogDescription>
					</DialogHeader>
					{viewing && (
						<div className="relative flex items-center justify-center">
							<img src={viewing.url ?? ''} alt="" className="max-h-[70vh] w-auto rounded-md object-contain" />
							{visible.length > 1 && (
								<>
									<Button
										type="button"
										variant="outline"
										size="icon"
										className="absolute left-2"
										aria-label={t('system.grid.prev')}
										onClick={() => setViewIndex(i => (i == null ? 0 : (i - 1 + visible.length) % visible.length))}>
										<ChevronLeft size={16} />
									</Button>
									<Button
										type="button"
										variant="outline"
										size="icon"
										className="absolute right-2"
										aria-label={t('system.grid.next')}
										onClick={() => setViewIndex(i => (i == null ? 0 : (i + 1) % visible.length))}>
										<ChevronRight size={16} />
									</Button>
								</>
							)}
						</div>
					)}
					<DialogFooter>
						<Button type="button" variant="destructive" onClick={() => setConfirmId(viewing?.id ?? null)}>
							<Trash2 size={15} /> {t('catalog.reviews.photos.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={confirmId != null} onOpenChange={open => !open && setConfirmId(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.reviews.photos.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.reviews.photos.deleteBody')}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => setConfirmId(null)}>
							{t('common.cancel')}
						</Button>
						<Button type="button" variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
